import { Component, HostListener, Injector, OnInit } from '@angular/core';
import { Auth, signOut } from '@angular/fire/auth';
import { AnnualReportProvider } from '@modeso/aumico-lib-annualreport-fe';
import {
    AnnouncementsProvider,
    AumicoCoreProvider,
    GetAnnouncementDto,
    SessionExpirationService,
    Survey,
    selectUserAnnouncements,
} from '@modeso/aumico-lib-core-fe';
import { AuthProvider, GetUserDto, OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store, select } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { Observable, Subscription, distinctUntilChanged, filter } from 'rxjs';
import {
    selectActiveRequestCount,
    selectActiveRequestIdentifiers,
} from '../../../aumico-lib-core-fe/src/lib/reducers/request-loading-indicator.reducer';
import {
    setDoNotShowAgainMessage,
    unsetDoNotShowAgainMessage,
} from '../../../aumico-lib-cpmm-fe/src/lib/actions/ui.actions';
import { selectDoNotShowAgainMessageState } from '../../../aumico-lib-cpmm-fe/src/lib/reducers/ui.reducer';
import { environment } from './../environments/environment';
import { ReleaseHighlightsService } from './shared/services/release-highlights.service';
import { VersionManagementService } from './shared/services/versionManagementService';
import { AppStateDispatcher } from './state/app.state.dispatcher';

const debug = Debug('aumico:project-report:AppComponent');

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
    title = 'Aumico Home Page';
    blockingRequestIsActive$: Observable<boolean>;
    // NEW: Add these for debugging
    activeRequestIdentifiers$: Observable<Array<{ identifier: string; activeCount: number; endedCount: number }>>;
    activeRequestCount$: Observable<number>;

    supportOperation = false;
    activeSurvey: Survey | undefined;
    userAnnouncements$: Observable<GetAnnouncementDto[]>;

    auth: Auth;
    authWarningIsDismissed: boolean = false;
    onSessionExpirationBannerSubscription: Subscription | null;

    constructor(
        private readonly injector: Injector,
        private coreProvider: AumicoCoreProvider,
        private readonly authProvider: AuthProvider,
        private readonly organizationProvider: OrganizationProvider,
        private readonly annualReportProvider: AnnualReportProvider,
        private readonly announcementsProvider: AnnouncementsProvider,
        private readonly appStateDispatcher: AppStateDispatcher,
        private versionManagementService: VersionManagementService,
        private readonly i18NextService: I18NextService,
        private readonly sessionExpirationService: SessionExpirationService,
        private readonly releaseHighlightsService: ReleaseHighlightsService,
        private store: Store, // Add Store injectio
    ) {
        this.auth = injector.get(Auth);
        this.appStateDispatcher
            .getSupportOperation()
            .pipe(untilDestroyed(this))
            .subscribe((supportOperation) => {
                this.supportOperation = supportOperation;
            });
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (
            event.ctrlKey &&
            event.altKey && // 'Alt' corresponds to 'Option' on Mac
            event.shiftKey &&
            event.key.toLowerCase() === 'x'
        ) {
            event.preventDefault();
            console.log('support operation key combination pressed');
            this.appStateDispatcher.setSupportOperation(!this.supportOperation);
            return;
        }
        if (event.metaKey && event.shiftKey && event.key.toLowerCase() === 's') {
            event.preventDefault();
            console.log('support operation key combination pressed');
            this.appStateDispatcher.setSupportOperation(!this.supportOperation);
            return;
        }
    }

    ngOnInit() {
        this.setDebuggingMode();
        this.blockingRequestIsActive$ = this.coreProvider.getBlockingRequestIsRunning$();
        this.versionManagementService.checkVersionAndUpdate();

        this.auth.onAuthStateChanged((credential) => {
            if (credential) {
                this.annualReportProvider.dispatchUpdatePlaceholderUserData(credential.displayName);
            }
        });

        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(
                filter((organization) => organization != null),
                untilDestroyed(this),
            )
            .subscribe((organization) => {
                this.annualReportProvider.dispatchUpdatePlaceholderOrganizationData(organization);
            });

        // NEW: Initialize debug selectors
        this.activeRequestIdentifiers$ = this.store.select(selectActiveRequestIdentifiers);
        this.activeRequestCount$ = this.store.select(selectActiveRequestCount);

        // Initialize user announcements selector
        this.userAnnouncements$ = this.store.select(selectUserAnnouncements);

        this.authProvider
            .getCurrentUser$()
            .pipe(
                filter((user) => user != null),
                distinctUntilChanged((prev, curr) => fastDeepEqual(prev, curr)),
                untilDestroyed(this),
            )
            .subscribe((user: GetUserDto) => {
                // Dispatch getUserAnnouncements as soon as user is available
                this.announcementsProvider.dispatchGetUserAnnouncements();

                this.releaseHighlightsService.showReleaseHighlights(user.lastSeenReleaseId);
            });
    }

    private setDebuggingMode(): void {
        localStorage['debug'] = environment.enableDebugging;
    }

    public onAnnouncementDismiss(announcementId: string): void {
        this.announcementsProvider.dispatchDismissAnnouncement(announcementId);
    }

    public trackByAnnouncementId(index: number, announcement: GetAnnouncementDto): string {
        return announcement.id;
    }

    public onReAuthenticate(): void {
        // Logout directly from Firebase Auth to trigger re-authentication
        signOut(this.auth)
            .then(() => {
                this.sessionExpirationService.logoutCleanup();
                window.location.reload();
            })
            .catch((error) => {
                console.error('[AppComponent] Firebase logout failed:', error);
            });
    }

    public getLocalizedMessage(announcement: GetAnnouncementDto): string {
        if (!announcement.message) {
            return '';
        }

        // Get current language from i18next
        const currentLanguage = this.i18NextService.language || 'de-ch';

        // Try to get message in current language
        if (announcement.message[currentLanguage]) {
            return announcement.message[currentLanguage];
        }

        // Return first non-empty message value if no matches found
        const keys = Object.keys(announcement.message);
        for (const key of keys) {
            const message = announcement.message[key];
            if (message && message.trim().length > 0) {
                return message;
            }
        }
        return '';
    }

    onSessionExpirationBannerIdChanged({ newId, oldId }: { newId: string | null; oldId: string | null }): void {
        // subscribe to the new id and destroy the subscription of the oldId
        if (this.onSessionExpirationBannerSubscription) {
            this.onSessionExpirationBannerSubscription.unsubscribe();
        }
        this.onSessionExpirationBannerSubscription = this.store
            .pipe(select(selectDoNotShowAgainMessageState(newId)))
            .subscribe((dismissed: boolean) => {
                this.authWarningIsDismissed = dismissed || false;
            });
    }

    dismissAuthWarning(messageId: string | null): void {
        this.store.dispatch(
            unsetDoNotShowAgainMessage({
                messageId: 'session-expiration-*',
            }),
        );
        this.store.dispatch(
            setDoNotShowAgainMessage({
                messageId: messageId,
                value: true,
            }),
        );
        this.authWarningIsDismissed = true;
    }
}
